import React, { useState, useEffect, useMemo } from 'react';
import { Button, Chip, Stack } from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { computeSideBySideDiff } from './diffUtils';
import classes from '../docFwb.module.css';

const FwbDiffViewer = ({
  oldText = '',
  newText = '',
  status = 'pending', // "pending" | "accepted" | "rejected"
  onAccept,
  onReject,
  acceptButtonRef,
  sideBySide = false,
  showViewToggle = false,
}) => {
  const [viewMode, setViewMode] = useState(sideBySide ? 'sideBySide' : 'unified');

  useEffect(() => {
    setViewMode(sideBySide ? 'sideBySide' : 'unified');
  }, [sideBySide]);

  const { diff, rows, addedCount, removedCount, hasChanges } = useMemo(
    () => computeSideBySideDiff(oldText, newText),
    [oldText, newText]
  );

  return (
    <div className={classes.diffCard}>
      {/* Diff Header */}
      <div className={classes.diffHeader}>
        <div className={classes.diffHeaderTitle}>
          <span>Suggested FWB Changes</span>
          <div className={classes.diffBadgeGroup}>
            <span className={classes.diffBadgeAdded}>+{addedCount}</span>
            <span className={classes.diffBadgeRemoved}>-{removedCount}</span>
          </div>
        </div>

        {showViewToggle && (
          <div className={classes.diffViewToggleGroup}>
            <button
              type='button'
              className={`${classes.diffViewToggleBtn} ${viewMode === 'sideBySide' ? classes.diffViewToggleBtnActive : ''}`}
              onClick={() => setViewMode('sideBySide')}
              title='Side-by-side comparison'
            >
              Side-by-Side
            </button>
            <button
              type='button'
              className={`${classes.diffViewToggleBtn} ${viewMode === 'unified' ? classes.diffViewToggleBtnActive : ''}`}
              onClick={() => setViewMode('unified')}
              title='Unified inline diff'
            >
              Unified
            </button>
          </div>
        )}
      </div>

      {viewMode === 'sideBySide' ? (
        <div className={classes.diffSideBySideContainer}>
          {/* Column Headers */}
          <div className={classes.diffSideBySideHeader}>
            <div className={`${classes.diffSideBySideHeaderCol} ${classes.diffSideBySideHeaderColLeft}`}>
              <span className={classes.diffSideBySideHeaderTitle}>Current FWB</span>
              {removedCount > 0 && (
                <span className={classes.diffBadgeRemoved}>-{removedCount} lines</span>
              )}
            </div>
            <div className={classes.diffSideBySideHeaderCol}>
              <span className={classes.diffSideBySideHeaderTitle}>Updated FWB</span>
              {addedCount > 0 && (
                <span className={classes.diffBadgeAdded}>+{addedCount} lines</span>
              )}
            </div>
          </div>

          {/* Side by Side Body */}
          <div className={classes.diffSideBySideBody} role='table'>
            {!hasChanges ? (
              <div className={classes.diffNoChanges}>No differences detected between Current and Updated FWB.</div>
            ) : (
              rows.map((row, index) => {
                const leftClass =
                  row.left.type === 'removed'
                    ? classes.diffSideBySideCellRemoved
                    : row.left.type === 'empty'
                      ? classes.diffSideBySideCellEmpty
                      : classes.diffSideBySideCellUnchanged;

                const rightClass =
                  row.right.type === 'added'
                    ? classes.diffSideBySideCellAdded
                    : row.right.type === 'empty'
                      ? classes.diffSideBySideCellEmpty
                      : classes.diffSideBySideCellUnchanged;

                const leftPrefix = row.left.type === 'removed' ? '-' : row.left.type === 'unchanged' ? ' ' : '';
                const rightPrefix = row.right.type === 'added' ? '+' : row.right.type === 'unchanged' ? ' ' : '';

                return (
                  <div key={index} className={classes.diffSideBySideRow} role='row'>
                    {/* Left Cell */}
                    <div className={`${classes.diffSideBySideCell} ${classes.diffSideBySideCellLeft} ${leftClass}`} role='cell'>
                      <span className={classes.diffLineNum}>{row.left.lineNumber || ''}</span>
                      <span className={classes.diffPrefix}>{leftPrefix}</span>
                      <span className={classes.diffLineText}>{row.left.line}</span>
                    </div>
                    {/* Right Cell */}
                    <div className={`${classes.diffSideBySideCell} ${rightClass}`} role='cell'>
                      <span className={classes.diffLineNum}>{row.right.lineNumber || ''}</span>
                      <span className={classes.diffPrefix}>{rightPrefix}</span>
                      <span className={classes.diffLineText}>{row.right.line}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : (
        /* Unified Content Body */
        <div className={classes.diffBody} role='list'>
          {!hasChanges ? (
            <div className={classes.diffNoChanges}>No differences detected between versions.</div>
          ) : (
            diff.map((item, index) => {
              const isAdded = item.type === 'added';
              const isRemoved = item.type === 'removed';
              const rowClass = isAdded
                ? classes.diffRowAdded
                : isRemoved
                  ? classes.diffRowRemoved
                  : classes.diffRowUnchanged;

              const prefix = isAdded ? '+' : isRemoved ? '-' : ' ';
              const lineNum = isAdded ? item.newLineNumber : isRemoved ? item.oldLineNumber : item.newLineNumber || item.oldLineNumber;

              return (
                <div key={`${index}-${item.type}-${item.line}`} className={`${classes.diffRow} ${rowClass}`} role='listitem'>
                  <span className={classes.diffLineNum}>{lineNum || ''}</span>
                  <span className={classes.diffPrefix}>{prefix}</span>
                  <span className={classes.diffLineText}>{item.line}</span>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Actions Bar */}
      <div className={classes.diffActionBar}>
        {status === 'accepted' ? (
          <Chip
            label='Accepted & Saved'
            color='success'
            size='small'
            variant='filled'
            sx={{
              fontSize: '11px',
              height: '24px',
            }}
          />
        ) : status === 'rejected' ? (
          <Chip
            label='Changes Rejected'
            color='default'
            size='small'
            variant='outlined'
            sx={{
              fontSize: '11px',
              height: '24px',
              color: '#94a3b8',
            }}
          />
        ) : (
          <Stack direction='row' spacing={1} justifyContent='flex-end' alignItems='center' width='100%'>
            {onReject && (
              <Button
                size='small'
                variant='outlined'
                color='inherit'
                onClick={onReject}
                startIcon={<Close sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: 'none',
                  fontSize: '11.5px',
                  py: '2px',
                  px: 1.5,
                  minHeight: '26px',
                  color: '#dc2626',
                  borderColor: '#fca5a5',
                  backgroundColor: '#ffffff',
                  '&:hover': {
                    borderColor: '#dc2626',
                    backgroundColor: '#fef2f2',
                  },
                }}
              >
                Reject 
              </Button>
            )}
            {onAccept && (
              <Button
                ref={acceptButtonRef}
                size='small'
                variant='contained'
                color='primary'
                onClick={onAccept}
                startIcon={<Check sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: 'none',
                  fontSize: '11.5px',
                  py: '2px',
                  px: 1.5,
                  minHeight: '26px',
                }}
              >
                Accept
              </Button>
            )}
          </Stack>
        )}
      </div>
    </div>
  );
};

export default FwbDiffViewer;
